import { defineMessages } from 'react-intl';

export const shortcutMarketplaceBrowseFilterMessages = defineMessages({
  filterAll: {
    defaultMessage: 'All',
    id: 'DIwxES+D/1',
    description: 'Browse filter tab: All',
  },
  filterSaved: {
    defaultMessage: 'Saved',
    id: 'sFRC41H9QL',
    description: 'Browse filter tab: Saved',
  },
  filterMyShortcuts: {
    defaultMessage: 'My shortcuts',
    id: 'bwhcfocGNm',
    description: 'Browse filter tab: MyShortcuts',
  },
  /**
   * "Guidelines" and "Management" track the level-1 starter taxonomy (Direction
   * C): a clinician who learns the vocabulary on the homepage navigates the
   * library with it. The CATEGORY keys stay `look_up` and `treat`, so no saved
   * shortcut is re-filed and no event is re-keyed.
   */
  filterLookUp: {
    defaultMessage: 'Guidelines',
    id: 'HyxW1PyqO2',
    description: 'Browse filter tab: LookUp',
  },
  filterWorkUp: {
    defaultMessage: 'Work up',
    id: 'ZWQWx7Uyap',
    description: 'Browse filter tab: WorkUp',
  },
  filterTreat: {
    defaultMessage: 'Management',
    id: 'hN2GxAZ541',
    description: 'Browse filter tab: Treat',
  },
  filterPrepare: {
    defaultMessage: 'Prepare',
    id: 'ohVyWa6lJG',
    description: 'Browse filter tab: Prepare',
  },
  filterWrite: {
    defaultMessage: 'Write',
    id: '+kodUrDI4w',
    description: 'Browse filter tab: Write',
  },
});

export const shortcutMarketplaceBuiltInMessages = defineMessages({
  prep_label: {
    defaultMessage: 'Prep',
    id: 'ymUlsK4fKQ',
    description: 'Built-in protocol shortcut chip label: prep',
  },
  prep_query: {
    defaultMessage:
      'Based on the patient’s history, what should I review or prepare before this consultation?',
    id: 'YY1aVLylw3',
    description: 'Built-in protocol prompt sent to Evidence: prep',
  },
  prep_description: {
    defaultMessage:
      'Pull together your patient’s key history, active problems, medications, and recent visits. Walk into the consultation informed instead of catching up on the fly.',
    id: 'wtpYj7RcXA',
    description: 'Built-in protocol marketplace card description: prep',
  },
  referralLetter_label: {
    defaultMessage: 'Referral Letter',
    id: 'eBTjKBZ0Zz',
    description: 'Built-in protocol shortcut chip label: referralLetter',
  },
  referralLetter_query: {
    defaultMessage:
      'Write a referral letter for this patient to send to a specialist. Include relevant history, findings, and reason for referral.',
    id: 'Jdwmb4lsHL',
    description: 'Built-in protocol prompt sent to Evidence: referralLetter',
  },
  referralLetter_description: {
    defaultMessage:
      'Draft a structured referral with relevant history, findings, and indication. Review, edit, and send instead of writing one from scratch.',
    id: 'UDBvh12dFd',
    description: 'Built-in protocol marketplace card description: referralLetter',
  },
  sickNote_label: {
    defaultMessage: 'Sick Note',
    id: '5lEc5VfIaY',
    description: 'Built-in protocol shortcut chip label: sickNote',
  },
  sickNote_query: {
    defaultMessage:
      'Write a medical certificate for this patient including diagnosis and recommended time off.',
    id: 'W+sgvuCHqR',
    description: 'Built-in protocol prompt sent to Evidence: sickNote',
  },
  sickNote_description: {
    defaultMessage:
      'Draft a medical certificate with diagnosis, clinical findings, and recommended period off work. Formatted and ready for your review.',
    id: 'G6EFJw7JII',
    description: 'Built-in protocol marketplace card description: sickNote',
  },
  patientLetter_label: {
    defaultMessage: 'Patient Letter',
    id: '3OLJLMZv8w',
    description: 'Built-in protocol shortcut chip label: patientLetter',
  },
  patientLetter_query: {
    defaultMessage:
      'Write a letter to the patient summarizing today’s visit, the plan, and next steps in plain language.',
    id: '/EaiADhiz7',
    description: 'Built-in protocol prompt sent to Evidence: patientLetter',
  },
  patientLetter_description: {
    defaultMessage:
      'Write a plain-language summary of the visit for your patient, covering what was discussed, the plan, and what to do next. Give them something clear to take home.',
    id: '1GE7ijzPAU',
    description: 'Built-in protocol marketplace card description: patientLetter',
  },
  translateSpanish_label: {
    defaultMessage: 'Translate Spanish',
    id: 'jILA24n5UO',
    description: 'Built-in protocol shortcut chip label: translateSpanish',
  },
  translateSpanish_query: {
    defaultMessage: 'Translate the key patient instructions and plan into Spanish.',
    id: '2tF4n73hg2',
    description: 'Built-in protocol prompt sent to Evidence: translateSpanish',
  },
  translateSpanish_description: {
    defaultMessage:
      'Translate your patient instructions and management plan into Spanish. Give your patient or their family clear instructions in the language they are comfortable with.',
    id: 'r5MUxIASWL',
    description: 'Built-in protocol marketplace card description: translateSpanish',
  },
  simplify_label: {
    defaultMessage: 'Simplify',
    id: 'aJIambGJ2d',
    description: 'Built-in protocol shortcut chip label: simplify',
  },
  simplify_query: {
    defaultMessage:
      'Rewrite the plan and instructions in simple, easy-to-understand language for the patient.',
    id: 'FNUlJGvIQv',
    description: 'Built-in protocol prompt sent to Evidence: simplify',
  },
  simplify_description: {
    defaultMessage:
      'Rewrite your plan and instructions in simple everyday language, without the medical jargon. Make it easy for your patient to understand what to do and why.',
    id: 'sEep5P4Etg',
    description: 'Built-in protocol marketplace card description: simplify',
  },
  icdCodes_label: {
    defaultMessage: 'ICD Codes',
    id: 'pz1pOptNBx',
    description: 'Built-in protocol shortcut chip label: icdCodes',
  },
  icdCodes_query: {
    defaultMessage:
      'What are the appropriate ICD-10 diagnosis codes for this consultation?',
    id: 'IE9aFnRaTC',
    description: 'Built-in protocol prompt sent to Evidence: icdCodes',
  },
  icdCodes_description: {
    defaultMessage:
      'Generate ICD-10 codes based on your consultation findings and working diagnoses. Spend less time on coding, move on to the next patient.',
    id: 'pm3rY7Hf8G',
    description: 'Built-in protocol marketplace card description: icdCodes',
  },
  billing_label: {
    defaultMessage: 'Billing',
    id: 'RsNyhuNCfz',
    description: 'Built-in protocol shortcut chip label: billing',
  },
  billing_query: {
    defaultMessage:
      'Summarize this visit for billing purposes including diagnoses and procedures performed.',
    id: 'BIOohSsTY1',
    description: 'Built-in protocol prompt sent to Evidence: billing',
  },
  billing_description: {
    defaultMessage:
      'Summarise the visit for billing, including diagnoses, procedures, and complexity level. Get accurate claims without re-reading the whole encounter.',
    id: 'SKfd0SdFHY',
    description: 'Built-in protocol marketplace card description: billing',
  },
  guideline_label: {
    defaultMessage: 'Guideline',
    id: 'znuoAJlCeb',
    description: 'Built-in protocol shortcut chip label: guideline',
  },
  guideline_query: {
    defaultMessage:
      'What do current guidelines recommend for the condition being discussed? Include the guideline name and source, key recommendations, and strength of evidence where available.',
    id: 'KRyJh7Xh+S',
    description: 'Built-in protocol prompt sent to Evidence: guideline',
  },
  guideline_description: {
    defaultMessage:
      'Find the relevant guideline recommendation for the condition being discussed. Get the current standard of care without leaving your workflow.',
    id: '+2tnLk0GVI',
    description: 'Built-in protocol marketplace card description: guideline',
  },
  dischargeSummary_label: {
    defaultMessage: 'Discharge Summary',
    id: 'zs9dZgjTUT',
    description: 'Built-in protocol shortcut chip label: dischargeSummary',
  },
  dischargeSummary_query: {
    defaultMessage:
      'Draft a discharge summary for this patient. Include reason for admission, hospital course, key investigations and findings, diagnoses, medications at discharge with any changes from admission, and follow-up plan.',
    id: 'GW0C7Gjnk4',
    description: 'Built-in protocol prompt sent to Evidence: dischargeSummary',
  },
  dischargeSummary_description: {
    defaultMessage:
      'Draft a discharge summary for an inpatient stay or ED visit, covering admission reason, course, findings, and follow-up plan. Ready for your review before sending.',
    id: '+14Oyxwgqg',
    description: 'Built-in protocol marketplace card description: dischargeSummary',
  },
  clinicalSummary_label: {
    defaultMessage: 'Clinical Summary',
    id: 'QBeB/mj42r',
    description: 'Built-in protocol shortcut chip label: clinicalSummary',
  },
  clinicalSummary_query: {
    defaultMessage:
      'Generate a concise clinical summary of this patient suitable for handover or case discussion. Include presenting complaint, key history, findings, working diagnosis, and current management plan.',
    id: 'AqCosxCiX0',
    description: 'Built-in protocol prompt sent to Evidence: clinicalSummary',
  },
  clinicalSummary_description: {
    defaultMessage:
      'Generate a concise clinical summary of this patient suitable for handover, case discussion, or your own reference. Covers the key facts without the noise.',
    id: 'WN3/PZOyYD',
    description: 'Built-in protocol marketplace card description: clinicalSummary',
  },
  drugCheckUs_label: {
    defaultMessage: 'Drug Check',
    id: '2AXQJFYIGB',
    description: 'Built-in protocol shortcut chip label: drugCheckUs',
  },
  drugCheckUs_query: {
    defaultMessage:
      'Check for any drug interactions or contraindications with this patient’s medications.',
    id: 'u8BUIpeyda',
    description: 'Built-in protocol prompt sent to Evidence: drugCheckUs',
  },
  drugCheckUs_description: {
    defaultMessage:
      'Check for drug interactions, contraindications, and safety concerns with your patient’s current medications. Flag anything that needs attention before you prescribe.',
    id: 'ljC9x+9vyu',
    description: 'Built-in protocol marketplace card description: drugCheckUs',
  },
  sideEffectsUs_label: {
    defaultMessage: 'Side Effects',
    id: 'r8nbtntiwi',
    description: 'Built-in protocol shortcut chip label: sideEffectsUs',
  },
  sideEffectsUs_query: {
    defaultMessage:
      'Could any of this patient’s symptoms be a side effect or adverse reaction to their medications? Review each medication for known adverse effects that match the clinical presentation, and note the likelihood and typical onset for each potential match.',
    id: 'TUeJoShPTk',
    description: 'Built-in protocol prompt sent to Evidence: sideEffectsUs',
  },
  sideEffectsUs_description: {
    defaultMessage:
      'Check whether your patient’s symptoms could be a medication side effect. Review their current medications for adverse effects that match the clinical picture.',
    id: 'h2Wu9QAiMf',
    description: 'Built-in protocol marketplace card description: sideEffectsUs',
  },
  checkDosingUs_label: {
    defaultMessage: 'Check Dosing',
    id: 'CXDCT4FOGw',
    description: 'Built-in protocol shortcut chip label: checkDosingUs',
  },
  checkDosingUs_query: {
    defaultMessage:
      'Check the dosing for medications discussed in this consultation. Include standard dosing, any adjustments needed for this patient (renal function, hepatic function, age, weight, pregnancy if relevant), and flag any doses that appear incorrect or need review.',
    id: '24yurfgqWR',
    description: 'Built-in protocol prompt sent to Evidence: checkDosingUs',
  },
  checkDosingUs_description: {
    defaultMessage:
      'Verify dosing, route, frequency, and adjustments for medications discussed in this consultation. Catch errors before they reach the prescription.',
    id: 'DpTAsWKqcD',
    description: 'Built-in protocol marketplace card description: checkDosingUs',
  },
  examUs_label: {
    defaultMessage: 'Exam',
    id: 'zcoH30ENhI',
    description: 'Built-in protocol shortcut chip label: examUs',
  },
  examUs_query: {
    defaultMessage: 'What physical examination should I perform for this patient?',
    id: 'FjuRL1mPwy',
    description: 'Built-in protocol prompt sent to Evidence: examUs',
  },
  examUs_description: {
    defaultMessage:
      'Suggest a focused examination based on the presenting complaint and history so far. Cover the key findings to look for so nothing relevant is missed.',
    id: 'cWNUgnww3q',
    description: 'Built-in protocol marketplace card description: examUs',
  },
  counsellingPointsUs_label: {
    defaultMessage: 'Counselling Points',
    id: 'cmIPm6SBdK',
    description: 'Built-in protocol shortcut chip label: counsellingPointsUs',
  },
  counsellingPointsUs_query: {
    defaultMessage:
      'What are the key points to discuss with this patient about their diagnosis and management plan? Cover what to expect, common patient questions, relevant lifestyle advice, and when to seek further help.',
    id: 'WICUPe3mFH',
    description: 'Built-in protocol prompt sent to Evidence: counsellingPointsUs',
  },
  counsellingPointsUs_description: {
    defaultMessage:
      'Get key counselling points to discuss with your patient about their diagnosis, treatment, and what to expect. Cover the questions patients commonly ask.',
    id: '22hAbdPQF7',
    description: 'Built-in protocol marketplace card description: counsellingPointsUs',
  },
  safetyNetUs_label: {
    defaultMessage: 'Safety Net',
    id: 'TyPP4Zdxkq',
    description: 'Built-in protocol shortcut chip label: safetyNetUs',
  },
  safetyNetUs_query: {
    defaultMessage:
      'What safety net advice should I give? When should the patient return or seek urgent care?',
    id: 'zwPkkQvH3L',
    description: 'Built-in protocol prompt sent to Evidence: safetyNetUs',
  },
  safetyNetUs_description: {
    defaultMessage:
      'Generate safety netting advice for your patient: when to come back, what to watch for, and when to seek urgent care. Essential for uncertain or evolving presentations.',
    id: 'XX7oQq4+5e',
    description: 'Built-in protocol marketplace card description: safetyNetUs',
  },
  followUpUs_label: {
    defaultMessage: 'Follow-up',
    id: 'udMKZQJ+/C',
    description: 'Built-in protocol shortcut chip label: followUpUs',
  },
  followUpUs_query: {
    defaultMessage:
      'What follow-up plan should I arrange? Include timing and what to review.',
    id: 't8hXJN0rh4',
    description: 'Built-in protocol prompt sent to Evidence: followUpUs',
  },
  followUpUs_description: {
    defaultMessage:
      'Plan the follow-up: timing, what to review next visit, and any interim investigations to arrange. Close the loop on your management plan.',
    id: 'cgt1lu4mUP',
    description: 'Built-in protocol marketplace card description: followUpUs',
  },
  riskScoreUs_label: {
    defaultMessage: 'Risk Score',
    id: 'VOyW7+nkR5',
    description: 'Built-in protocol shortcut chip label: riskScoreUs',
  },
  riskScoreUs_query: {
    defaultMessage:
      'Identify and calculate relevant clinical risk scores or validated decision tools for this patient’s presentation. Show the inputs used and their values, the calculated score, and what it means. Flag any missing data needed for a complete calculation.',
    id: 'Glw2umaT/e',
    description: 'Built-in protocol prompt sent to Evidence: riskScoreUs',
  },
  riskScoreUs_description: {
    defaultMessage:
      'Calculate a clinical risk score using your patient’s data. Covers validated tools like CHA2DS2-VASc, Wells, CURB-65, and others to support guideline-concordant decisions.',
    id: 'Q3EWYztEOv',
    description: 'Built-in protocol marketplace card description: riskScoreUs',
  },
  questionsUs_label: {
    defaultMessage: 'Questions',
    id: 'ykqIlmcoyj',
    description: 'Built-in protocol shortcut chip label: questionsUs',
  },
  questionsUs_query: {
    defaultMessage:
      'Suggest follow-up questions to ask the patient based on the consultation so far.',
    id: 'kdassCyLBu',
    description: 'Built-in protocol prompt sent to Evidence: questionsUs',
  },
  questionsUs_description: {
    defaultMessage:
      'Surface targeted follow-up questions based on the consultation so far. Catch gaps in the history before your patient leaves.',
    id: 'w8fKBLcjmY',
    description: 'Built-in protocol marketplace card description: questionsUs',
  },
  interactionsNonUs_label: {
    defaultMessage: 'Drug Interactions',
    id: 'kwMY8qGRkM',
    description: 'Built-in protocol shortcut chip label: interactionsNonUs',
  },
  interactionsNonUs_query: {
    defaultMessage:
      'Look up known drug interactions for the medications this patient is taking or being considered for. List clinically significant interactions, severity, and any recommended monitoring or alternatives.',
    id: '9Bg71gVIlC',
    description: 'Built-in protocol prompt sent to Evidence: interactionsNonUs',
  },
  interactionsNonUs_description: {
    defaultMessage:
      'Look up drug interaction data for your patient’s current and proposed medications. Get severity ratings, monitoring advice, and alternative suggestions.',
    id: '8fH9A2RW0H',
    description: 'Built-in protocol marketplace card description: interactionsNonUs',
  },
  sideEffectsNonUs_label: {
    defaultMessage: 'Side Effects',
    id: 'deMJqDsFZ4',
    description: 'Built-in protocol shortcut chip label: sideEffectsNonUs',
  },
  sideEffectsNonUs_query: {
    defaultMessage:
      'Look up the common and serious side effects for the medications discussed in this consultation. For each drug, list notable adverse effects, their approximate frequency, and any that require urgent attention.',
    id: 'vV5mi2VVs3',
    description: 'Built-in protocol prompt sent to Evidence: sideEffectsNonUs',
  },
  sideEffectsNonUs_description: {
    defaultMessage:
      'Look up side-effect profiles for medications under discussion. Covers frequency, severity, and anything needing urgent monitoring.',
    id: 'eCPuedG8e0',
    description: 'Built-in protocol marketplace card description: sideEffectsNonUs',
  },
  dosingNonUs_label: {
    defaultMessage: 'Dosing',
    id: 'gNtbzz7VDk',
    description: 'Built-in protocol shortcut chip label: dosingNonUs',
  },
  dosingNonUs_query: {
    defaultMessage:
      'Look up the recommended dosing for the medications discussed in this consultation. Include standard adult dosing, route, frequency, and note any adjustments for renal impairment, hepatic impairment, age, or weight where relevant.',
    id: 'Sdr/40B9KW',
    description: 'Built-in protocol prompt sent to Evidence: dosingNonUs',
  },
  dosingNonUs_description: {
    defaultMessage:
      'Look up recommended dosing, route, and frequency for medications under discussion. Includes adjustment guidance for renal/hepatic impairment.',
    id: 'IohBXuHruf',
    description: 'Built-in protocol marketplace card description: dosingNonUs',
  },
  examNonUs_label: {
    defaultMessage: 'Exam',
    id: 'vlbU1j6mBU',
    description: 'Built-in protocol shortcut chip label: examNonUs',
  },
  examNonUs_query: {
    defaultMessage:
      'What examination findings would help narrow the differential for this patient’s presentation? List the key examination components and what positive or negative findings to look for.',
    id: 'TvSLfxpMcg',
    description: 'Built-in protocol prompt sent to Evidence: examNonUs',
  },
  examNonUs_description: {
    defaultMessage:
      'Identify the examination components most useful for this presentation. See what findings to look for and how they narrow the differential.',
    id: 'DX9V8elPpD',
    description: 'Built-in protocol marketplace card description: examNonUs',
  },
  patientTalkingPointsNonUs_label: {
    defaultMessage: 'Patient Talking Points',
    id: 'Fyq5VoSjIg',
    description: 'Built-in protocol shortcut chip label: patientTalkingPointsNonUs',
  },
  patientTalkingPointsNonUs_query: {
    defaultMessage:
      'What are the key points to cover when explaining this condition and management plan to the patient? Include what to expect, common questions, and relevant lifestyle advice.',
    id: 'incj3S28L+',
    description: 'Built-in protocol prompt sent to Evidence: patientTalkingPointsNonUs',
  },
  patientTalkingPointsNonUs_description: {
    defaultMessage:
      'Get a structured set of talking points for your patient conversation, covering diagnosis, management, expectations, and common questions.',
    id: 'eZiUxhPaV9',
    description:
      'Built-in protocol marketplace card description: patientTalkingPointsNonUs',
  },
  safetyNettingNonUs_label: {
    defaultMessage: 'Safety Netting',
    id: 'tWPTigE9ix',
    description: 'Built-in protocol shortcut chip label: safetyNettingNonUs',
  },
  safetyNettingNonUs_query: {
    defaultMessage:
      'What safety-netting advice is recommended for this presentation? Include specific symptoms or changes the patient should watch for, when to seek urgent review, and any recommended timeframe for routine follow-up.',
    id: 'aOmK9N8hM4',
    description: 'Built-in protocol prompt sent to Evidence: safetyNettingNonUs',
  },
  safetyNettingNonUs_description: {
    defaultMessage:
      'Get evidence-based safety-netting advice for this presentation: what to watch for, when to return, and when to seek urgent care.',
    id: 'yTqCU6jiDE',
    description: 'Built-in protocol marketplace card description: safetyNettingNonUs',
  },
  followUpNonUs_label: {
    defaultMessage: 'Follow-up',
    id: 'HmSHqQDwm4',
    description: 'Built-in protocol shortcut chip label: followUpNonUs',
  },
  followUpNonUs_query: {
    defaultMessage:
      'What does the evidence suggest for follow-up of this condition? Include recommended timing, what to reassess, and any investigations to arrange before the next review.',
    id: 'BP5c8qWnC8',
    description: 'Built-in protocol prompt sent to Evidence: followUpNonUs',
  },
  followUpNonUs_description: {
    defaultMessage:
      'Look up recommended follow-up intervals, what to reassess, and any interim investigations to arrange.',
    id: 'LEXBw6VKfv',
    description: 'Built-in protocol marketplace card description: followUpNonUs',
  },
  clinicalScoresNonUs_label: {
    defaultMessage: 'Clinical Scores',
    id: 'McpgsAtOu0',
    description: 'Built-in protocol shortcut chip label: clinicalScoresNonUs',
  },
  clinicalScoresNonUs_query: {
    defaultMessage:
      'What validated clinical scores or decision tools are relevant to this patient’s presentation? For each tool, list the required inputs, calculate the score using available data, and explain the interpretation. Flag any missing data needed.',
    id: 'nUdIvqGuL6',
    description: 'Built-in protocol prompt sent to Evidence: clinicalScoresNonUs',
  },
  clinicalScoresNonUs_description: {
    defaultMessage:
      'Identify and calculate relevant clinical risk scores for this presentation. Covers validated tools with interpretation and flagging of missing inputs.',
    id: 'eD+YLFxx3x',
    description: 'Built-in protocol marketplace card description: clinicalScoresNonUs',
  },
  questionsNonUs_label: {
    defaultMessage: 'Questions',
    id: 'oPzZtyuM2m',
    description: 'Built-in protocol shortcut chip label: questionsNonUs',
  },
  questionsNonUs_query: {
    defaultMessage:
      'Based on the history so far, what additional questions would help clarify the diagnosis or guide management? Focus on gaps in the current information.',
    id: 'FfbghDz9rh',
    description: 'Built-in protocol prompt sent to Evidence: questionsNonUs',
  },
  questionsNonUs_description: {
    defaultMessage:
      'Identify gaps in the history and suggest targeted follow-up questions to clarify the clinical picture.',
    id: 'lHtL6fALsC',
    description: 'Built-in protocol marketplace card description: questionsNonUs',
  },
  ddx_label: {
    defaultMessage: 'DDx',
    id: 'Duz3OhzdLx',
    description: 'Built-in protocol shortcut chip label: ddx',
  },
  ddx_query: {
    defaultMessage:
      'What differential diagnoses should I consider? Introduce each diagnosis with a bullet point.',
    id: 'koU0AFJgn8',
    description: 'Built-in protocol prompt sent to Evidence: ddx',
  },
  ddx_description: {
    defaultMessage:
      'Generate a ranked differential based on the history and findings so far. Consider diagnoses you might anchor away from, including less common but critical conditions.',
    id: 'mCN/Ym5dGn',
    description: 'Built-in protocol marketplace card description: ddx',
  },
  redFlags_label: {
    defaultMessage: 'Red Flags',
    id: '1tW/vJzRA5',
    description: 'Built-in protocol shortcut chip label: redFlags',
  },
  redFlags_query: {
    defaultMessage: 'Are there any red flags or dangerous conditions I should rule out?',
    id: 'ctPFTQACmK',
    description: 'Built-in protocol prompt sent to Evidence: redFlags',
  },
  redFlags_description: {
    defaultMessage:
      'Review the presentation for red flags and serious conditions to rule out. A second pair of eyes on what could be dangerous to miss.',
    id: 'tmDpYbQXZP',
    description: 'Built-in protocol marketplace card description: redFlags',
  },
  plan_label: {
    defaultMessage: 'Plan',
    id: 'QmtTV6RGt0',
    description: 'Built-in protocol shortcut chip label: plan',
  },
  plan_query: {
    defaultMessage: 'Suggest a treatment plan based on the visit so far.',
    id: 'sdn4bxYqrZ',
    description: 'Built-in protocol prompt sent to Evidence: plan',
  },
  plan_description: {
    defaultMessage:
      'Get a management plan based on your working diagnosis, including treatment options, lifestyle advice, and next steps. A starting point to refine, not a final answer.',
    id: 'jsgbT4NR9V',
    description: 'Built-in protocol marketplace card description: plan',
  },
  tests_label: {
    defaultMessage: 'Tests',
    id: 'QCeho4qAK8',
    description: 'Built-in protocol shortcut chip label: tests',
  },
  tests_query: {
    defaultMessage: 'What tests or investigations should I order for this patient?',
    id: 'JyU1k4Lg4z',
    description: 'Built-in protocol prompt sent to Evidence: tests',
  },
  tests_description: {
    defaultMessage:
      'Recommend investigations based on the clinical presentation and symptoms under consideration. Includes rationale so you can prioritise what to order.',
    id: 'P/AVOcht7C',
    description: 'Built-in protocol marketplace card description: tests',
  },
  prescribe_label: {
    defaultMessage: 'Prescribe',
    id: 'eRRjFlso0W',
    description: 'Built-in protocol shortcut chip label: prescribe',
  },
  prescribe_query: {
    defaultMessage:
      'What medications should I consider prescribing? Include dosing and duration.',
    id: 'lsYnqvnFwD',
    description: 'Built-in protocol prompt sent to Evidence: prescribe',
  },
  prescribe_description: {
    defaultMessage:
      'Get medication suggestions for your working diagnosis, including dosing, route, and duration. Cross-referenced against your patient’s history for safety.',
    id: 'EaASaMVGY3',
    description: 'Built-in protocol marketplace card description: prescribe',
  },
  interpretResults_label: {
    defaultMessage: 'Interpret Results',
    id: 'b/N2x+Ukv0',
    description: 'Built-in protocol shortcut chip label: interpretResults',
  },
  interpretResults_query: {
    defaultMessage:
      'Interpret the test results or investigation findings discussed in this consultation. Highlight any abnormal or concerning values, explain what they mean in this patient’s clinical context, and suggest appropriate next steps. If specific results aren’t mentioned, indicate what information would be needed.',
    id: 'b/VoNZnPOm',
    description: 'Built-in protocol prompt sent to Evidence: interpretResults',
  },
  interpretResults_description: {
    defaultMessage:
      'Interpret test results or investigation findings in your patient’s clinical context. Get an assessment of what the results mean and what to do next.',
    id: 'jbm1kz9ePf',
    description: 'Built-in protocol marketplace card description: interpretResults',
  },
  compareTreatments_label: {
    defaultMessage: 'Compare Treatments',
    id: 'RBfnL/Nhbv',
    description: 'Built-in protocol shortcut chip label: compareTreatments',
  },
  compareTreatments_query: {
    defaultMessage:
      'Compare the main treatment options for this patient’s condition. For each option, cover efficacy, common side effects, contraindications, and which patient factors favour it. Note any cost or availability considerations.',
    id: 'f12UncTH3t',
    description: 'Built-in protocol prompt sent to Evidence: compareTreatments',
  },
  compareTreatments_description: {
    defaultMessage:
      'Compare treatment options head-to-head for the current diagnosis, covering efficacy, side effects, cost, and patient factors. Support shared decision-making with your patient.',
    id: 'lwrzhYMyK7',
    description: 'Built-in protocol marketplace card description: compareTreatments',
  },
  stepUpTherapy_label: {
    defaultMessage: 'Step-Up Therapy',
    id: 'BDDzHEqG66',
    description: 'Built-in protocol shortcut chip label: stepUpTherapy',
  },
  stepUpTherapy_query: {
    defaultMessage:
      'What are the next-step treatment options for this patient’s condition if the current approach needs escalation? Include step-up options, second-line alternatives, and adjunct therapies. Cover when to escalate versus switch, and any monitoring needed with each option.',
    id: 'G9FXWOpcMJ',
    description: 'Built-in protocol prompt sent to Evidence: stepUpTherapy',
  },
  stepUpTherapy_description: {
    defaultMessage:
      'Get step-up options when first-line treatment is not enough. Includes second-line therapies, adjuncts, and guidance on when to escalate versus switch.',
    id: 'MUxFMvvOkW',
    description: 'Built-in protocol marketplace card description: stepUpTherapy',
  },
});
